<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BaseModal from '@/components/Shared/Modals/BaseModal.vue';
import SuccessIcon from '@/assets/svg/add-user-success.svg?component';
import ResumeDropzone from '@/components/Shared/ResumeUpload/ResumeDropzone.vue';
import ResumeFileList from '@/components/Shared/ResumeUpload/ResumeFileList.vue';
import EngagementModeSelector from '@/components/Shared/ResumeUpload/EngagementModeSelector.vue';
import SendCandidatesHereSection from '@/components/Shared/ResumeUpload/SendCandidatesHereSection.vue';
import { useResumeUpload } from '@/composables/useResumeUpload';
import { ErrorService } from '@/core/shared/errors/error.service';
import { OutreachService, type ResumeUploadFile } from '@/core/sourcing/outreach/outreach.service';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';

const emit = defineEmits<{
  upload: [];
}>();

const dialogService = new DialogService();
const outreachService = new OutreachService();

const modalProps = computed(() => dialogService.uploadResumesModalProps);
const isOpen = computed(() => modalProps.value.isOpen);
const jobId = computed(() => (modalProps.value.isOpen ? modalProps.value.jobId : undefined));
const source = computed(() => (modalProps.value.isOpen ? modalProps.value.source : undefined));
const isContactModeEnabled = computed(
  () => modalProps.value.isOpen && modalProps.value.activeOutreachEnabled,
);

watch(isOpen, (opened) => {
  if (opened) {
    TrackingService.trackAction(TrackingActionName.RESUME_UPLOAD_MODAL_OPENED, {
      source: source.value,
      job_id: jobId.value,
    });
  }
});

const { selectedFiles, selectedOption, addFiles, removeFile, clearAllFiles, toBase64, resetState } =
  useResumeUpload();

const isUploading = ref(false);
const showSuccess = ref(false);
const uploadedCount = ref(0);

async function handleSubmit() {
  if (selectedFiles.value.length === 0 || jobId.value === undefined) {
    return;
  }

  isUploading.value = true;

  try {
    // Convert all files to base64 in parallel
    const filesWithBase64: ResumeUploadFile[] = await Promise.all(
      selectedFiles.value.map(async (file) => {
        const base64Data = await toBase64(file);
        return {
          file,
          base64Data: base64Data as string,
        };
      }),
    );

    // Upload all resumes
    const { successCount } = await outreachService.uploadCandidateResumes(
      jobId.value,
      filesWithBase64,
      selectedOption.value,
      source.value ? { source: source.value } : undefined,
    );

    if (successCount > 0) {
      uploadedCount.value = successCount;
      emit('upload');
      showSuccess.value = true;
    }
  } catch (error) {
    ErrorService.captureException(error);
  } finally {
    isUploading.value = false;
  }
}

function handleSuccessClose() {
  handleClose();
}

function handleClose() {
  dialogService.uploadResumesModalProps = { isOpen: false };
  resetState();
  showSuccess.value = false;
  isUploading.value = false;
  uploadedCount.value = 0;
}
</script>

<template>
  <BaseModal
    :model-value="isOpen"
    max-width="640px"
    :max-height="showSuccess ? '317px' : 'auto'"
    :title="showSuccess ? '' : 'Upload candidates'"
    :subtitle="showSuccess ? '' : 'Add external candidates to this job by uploading their resumes.'"
    submit-button-text="Upload candidates"
    :is-submit-disabled="selectedFiles.length === 0"
    :is-loading="isUploading"
    :show-close-button="true"
    :show-cancel-button="!showSuccess && !isUploading"
    :show-actions="!showSuccess"
    @close="handleClose"
    @cancel="handleClose"
    @submit="handleSubmit"
  >
    <!-- Success State -->
    <div v-if="showSuccess" class="flex flex-col items-center">
      <!-- Content section with icon and text -->
      <div class="flex w-full flex-col items-center gap-2">
        <!-- Success icon -->
        <SuccessIcon class="mb-2 h-24 w-24" />

        <!-- Success message -->
        <h3 class="text-center font-sans text-2xl font-black leading-[29px] text-highlight-1000">
          Adding {{ uploadedCount }} candidate{{ uploadedCount > 1 ? 's' : '' }} to your job
        </h3>
        <p class="mt-2 max-w-[488px] text-center font-sans text-base leading-6 text-shade-600">
          Your resumes are being processed, candidates will appear in<br />
          your pipeline within 24 hours.
        </p>
      </div>

      <!-- Got it button -->
      <button
        type="button"
        class="mt-6 rounded-md bg-black px-3 py-2.5 font-sans text-sm font-semibold leading-5 tracking-[0.28px] text-white hover:bg-shade-900"
        @click="handleSuccessClose"
      >
        Got it
      </button>
    </div>

    <!-- Upload Form (shown when not in success state) -->
    <div v-if="!showSuccess" class="flex flex-col gap-6 pt-6">
      <!-- Send candidates here (per-job intake email address) -->
      <SendCandidatesHereSection v-if="jobId" :job-id="jobId" />

      <!-- Upload dropzone -->
      <ResumeDropzone @files-selected="addFiles" />

      <!-- Files list -->
      <ResumeFileList :files="selectedFiles" @remove-file="removeFile" @clear-all="clearAllFiles" />

      <!-- AI Recruiter Options -->
      <EngagementModeSelector
        :model-value="selectedOption"
        :is-contact-mode-enabled="isContactModeEnabled"
        @update:model-value="selectedOption = $event"
      />
    </div>
  </BaseModal>
</template>
